import { createGlobalState } from '@vueuse/core';
import type { AiLessonHelperMessage } from '~/types/ai-lesson-helper.types';

const useAiLessonHelperStore = createGlobalState(() => {
  const state = reactive<{
    historyByLesson: Record<string, AiLessonHelperMessage[]>;
    chatIdByLesson: Record<string, string>;
    token: string | null;
  }>({
    historyByLesson: {},
    chatIdByLesson: {},
    token: null,
  });

  const token = computed(() => state.token);

  const getHistory = (lessonId: string): Readonly<AiLessonHelperMessage[]> =>
    readonly(state.historyByLesson[lessonId] ?? []) as Readonly<AiLessonHelperMessage[]>;

  const getChatId = (lessonId: string): string | null =>
    state.chatIdByLesson[lessonId] ?? null;

  const setToken = (value: string | null) => {
    state.token = value;
  };

  const setChatId = (lessonId: string, chatId: string) => {
    state.chatIdByLesson[lessonId] = chatId;
  };

  const pushMessage = (lessonId: string, message: AiLessonHelperMessage) => {
    if (!state.historyByLesson[lessonId]) {
      state.historyByLesson[lessonId] = [];
    }
    state.historyByLesson[lessonId].push(message);
  };

  const replaceMessage = (lessonId: string, id: number, message: AiLessonHelperMessage) => {
    const history = state.historyByLesson[lessonId];
    if (!history) {
      return;
    }
    const index = history.findIndex(entry => entry.id === id);
    if (index === -1) {
      return;
    }
    history.splice(index, 1, { ...message, id });
  };

  const removeMessages = (lessonId: string, ids: number[]) => {
    const history = state.historyByLesson[lessonId];
    if (!history) {
      return;
    }
    state.historyByLesson[lessonId] = history.filter(
      entry => entry.id === undefined || !ids.includes(entry.id),
    );
  };

  const clear = (lessonId: string) => {
    state.historyByLesson[lessonId] = [];
  };

  const clearChatId = (lessonId: string) => {
    delete state.chatIdByLesson[lessonId];
  };

  const reset = () => {
    state.historyByLesson = {};
    state.chatIdByLesson = {};
    state.token = null;
  };

  return {
    token,
    getHistory,
    getChatId,
    setToken,
    setChatId,
    pushMessage,
    replaceMessage,
    removeMessages,
    clear,
    clearChatId,
    reset,
  };
});

export default useAiLessonHelperStore;
